import { useEffect, useState } from "react";
import { Users, MousePointerClick, Eye, Megaphone, MessageCircle, Vote, Loader2, Radio, Tv, Info, Clock, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";
import { adminApi } from "@/lib/api";

const nf = new Intl.NumberFormat("pt-BR");
const fmtMin = (s) => (s >= 3600 ? `${Math.floor(s / 3600)}h ${Math.round((s % 3600) / 60)}min` : `${Math.round(s / 60)}min ${s % 60}s`);
const fmtAt = (iso) => (iso ? new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "—");

const StatCard = ({ label, value, sub, icon: Icon, tone = "white", testId }) => {
  const bgs = { blue: "bg-[var(--fi-blue)] text-white", red: "bg-[var(--fi-red)] text-white", white: "bg-white text-[var(--fi-blue)]" };
  return (
    <div className={`rounded-xl p-4 sm:p-5 shadow-[0_8px_30px_rgba(13,27,75,0.08)] ${bgs[tone]}`} data-testid={testId}>
      <div className="flex items-center justify-between opacity-80 text-[11px] uppercase tracking-widest font-bold"><span>{label}</span><Icon className="w-4 h-4" /></div>
      <div className="font-display text-3xl sm:text-4xl font-black mt-2 leading-none">{value}</div>
      {sub && <div className="text-xs opacity-70 mt-1.5">{sub}</div>}
    </div>
  );
};

const RANGES = [["today", "Hoje"], ["7d", "7 dias"], ["30d", "30 dias"], ["custom", "Personalizado"]];

const AudiencePanel = () => {
  const [channel, setChannel] = useState("radio");
  const [range, setRange] = useState("today");
  const [from, setFrom] = useState(() => new Date(Date.now() - 6 * 864e5).toISOString().slice(0, 10));
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const params = { channel, range };
        if (range === "custom") Object.assign(params, { from, to });
        const r = await adminApi.get("/admin/analytics/audience", { params });
        if (alive) { setData(r.data); setErr(null); }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Falha ao carregar audiência");
      }
    };
    load();
    const t = setInterval(load, 20000);
    return () => { alive = false; clearInterval(t); };
  }, [channel, range, from, to]);

  const label = channel === "radio" ? "Ouvintes" : "Espectadores";
  return (
    <section className="mt-8" data-testid="audience-panel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Audiência real</div>
          <h2 className="font-display text-2xl font-extrabold text-[var(--fi-blue)]">{channel === "radio" ? "Rádio" : "TV"}</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="flex rounded-full border border-gray-200 bg-white p-0.5">
            {[["radio", "Rádio", Radio], ["tv", "TV", Tv]].map(([id, l, Icon]) => (
              <button key={id} data-testid={`audience-channel-${id}`} onClick={() => setChannel(id)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest font-bold ${channel === id ? "bg-[var(--fi-blue)] text-white" : "text-gray-600"}`}><Icon className="w-3.5 h-3.5" />{l}</button>
            ))}
          </div>
          <div className="flex rounded-full border border-gray-200 bg-white p-0.5">
            {RANGES.map(([id, l]) => (
              <button key={id} data-testid={`audience-range-${id}`} onClick={() => setRange(id)} className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest font-bold ${range === id ? "bg-[var(--fi-red)] text-white" : "text-gray-600"}`}>{l}</button>
            ))}
          </div>
        </div>
      </div>
      {range === "custom" && (
        <div className="flex flex-wrap items-center gap-2 mb-4 text-sm">
          <input data-testid="audience-from" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-200 bg-white" />
          <span className="text-gray-400">até</span>
          <input data-testid="audience-to" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-200 bg-white" />
        </div>
      )}
      <div className="flex items-start gap-2 text-xs text-gray-500 bg-white rounded-lg p-3 border border-gray-100 mb-4" data-testid="audience-source-note">
        <Info className="w-4 h-4 shrink-0 text-[var(--fi-blue)]" />
        <span><strong>Fonte: aplicativo.</strong> Contagem feita pelo servidor a partir dos sinais que o player envia a cada 20 s enquanto toca (ID anônimo com hash, sem IP). Não inclui quem ouve pelo site de terceiros ou por outros players — esse total vem do servidor de streaming e pode ser integrado depois.</span>
      </div>
      {err && <div className="text-sm text-[var(--fi-red)] mb-4">{err}</div>}
      {!data ? (
        <div className="flex items-center justify-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Carregando…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label={`${label} agora`} value={nf.format(data.live_now)} sub="Ao vivo neste momento" icon={Users} tone="red" testId="audience-live-now" />
            <StatCard label="Pico hoje" value={nf.format(data.peak_today.value)} sub={data.peak_today.at ? `às ${fmtAt(data.peak_today.at)}` : "Sem registros hoje"} icon={TrendingUp} tone="blue" testId="audience-peak-today" />
            <StatCard label="Únicos no período" value={nf.format(data.unique.period)} sub={`${nf.format(data.sessions)} sessões`} icon={Users} testId="audience-unique-period" />
            <StatCard label="Tempo médio" value={data.avg_duration_s ? fmtMin(data.avg_duration_s) : "—"} sub={channel === "radio" ? "de escuta por sessão" : "de visualização por sessão"} icon={Clock} testId="audience-avg-duration" />
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3">
            {[["Hoje", data.unique.day, "audience-unique-day"], ["7 dias", data.unique.week, "audience-unique-week"], ["30 dias", data.unique.month, "audience-unique-month"]].map(([l, v, tid]) => (
              <div key={l} className="rounded-xl bg-white p-3 shadow-[0_6px_20px_rgba(13,27,75,0.05)]" data-testid={tid}>
                <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Únicos · {l}</div>
                <div className="font-display text-2xl font-black text-[var(--fi-blue)]">{nf.format(v)}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <div className="rounded-xl bg-white p-4 shadow-[0_8px_30px_rgba(13,27,75,0.06)]" data-testid="audience-hourly-chart">
              <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-3">Horários de maior audiência (únicos por hora)</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.hourly} margin={{ left: -20, right: 4 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={2} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => [nf.format(v), label]} />
                  <Bar dataKey="unique" fill="#d72638" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="rounded-xl bg-white p-4 shadow-[0_8px_30px_rgba(13,27,75,0.06)]" data-testid="audience-daily-chart">
              <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-3">Por dia — únicos e pico de simultâneos</div>
              {data.daily.length === 0 ? (
                <div className="h-[220px] flex items-center justify-center text-sm text-gray-400">Sem dados no período.</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={data.daily.map((d) => ({ ...d, label: d.date.slice(5).split("-").reverse().join("/") }))} margin={{ left: -20, right: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="unique" name="Únicos" stroke="#0d1b4b" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="peak" name="Pico simultâneos" stroke="#d72638" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
};

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await adminApi.get("/admin/analytics/summary");
        if (mounted) setData(data);
      } catch {}
    };
    load();
    const t = setInterval(load, 20000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  if (!data) {
    return <div className="flex items-center justify-center py-24 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Carregando relatório…</div>;
  }

  return (
    <div>
      <div className="text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold mb-1">Painel</div>
      <h1 className="font-display text-4xl sm:text-5xl font-black uppercase text-[var(--fi-blue)] leading-tight">Dashboard</h1>
      <p className="text-gray-500 mt-1">Números reais do aplicativo, atualizados a cada 20 segundos.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        <StatCard label="Rádio agora" value={nf.format(data.audience.radio)} sub="ouvintes no app" icon={Radio} tone="blue" testId="summary-radio-live" />
        <StatCard label="TV agora" value={nf.format(data.audience.tv)} sub="espectadores no app" icon={Tv} tone="red" testId="summary-tv-live" />
        <StatCard label="Impressões de anúncios" value={nf.format(data.ads.impressions)} sub={`${nf.format(data.ads.clicks)} cliques · CTR ${data.ads.ctr}% · ${nf.format(data.ads.active)} ativos`} icon={Eye} />
        <StatCard label="Votos em enquetes" value={nf.format(data.engagement.poll_votes)} sub={`${nf.format(data.engagement.polls)} enquete(s)`} icon={Vote} />
      </div>

      <AudiencePanel />

      <div className="rounded-xl bg-white p-5 shadow-[0_8px_30px_rgba(13,27,75,0.06)] mt-8">
        <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Cidadão & conteúdo</div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[["Reclamações", data.engagement.complaints, MessageCircle, "text-[var(--fi-red)]"], ["Pautas", data.engagement.reports, MousePointerClick, "text-[var(--fi-blue)]"], ["Episódios publicados", data.engagement.episodes_published, Megaphone, "text-emerald-600"], ["Anúncios cadastrados", data.ads.total, Megaphone, "text-amber-600"]].map(([l, v, Icon, c]) => (
            <div key={l} className="rounded-lg bg-gray-50 p-3 flex items-center gap-2">
              <Icon className={`w-5 h-5 ${c}`} />
              <div><div className="text-[10px] uppercase tracking-widest font-bold text-gray-500">{l}</div><div className="font-display text-xl font-extrabold text-[var(--fi-blue)]">{nf.format(v)}</div></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
